// The per-row PII reveal state shared by Kiriman and Pengirim (SEC-05).
//
// The list API returns masked values only. "Tampilkan" asks the reveal route
// for that one row, the first time it is opened; the answer is kept in this
// hook's memory (component state, never web storage) so closing and opening
// the row again does not ask again. The cache dies with the screen.
import { useCallback, useRef, useState } from "react";
import { ApiError, isUnauthorized } from "./api";
import { useOnUnauthorized } from "./data";

export const REVEAL_RATE_MESSAGE = "Terlalu banyak permintaan buka data, coba lagi nanti";
export const REVEAL_UNAVAILABLE_MESSAGE = "Batas akses sedang tidak tersedia, coba lagi sebentar";
export const REVEAL_GENERIC_MESSAGE = "Data belum bisa dibuka. Coba lagi sebentar lagi.";

/** The inline message for a failed reveal, by status and error code. */
export function revealMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return REVEAL_RATE_MESSAGE;
    if (err.status === 503 && err.message === "rate_limit_unavailable") return REVEAL_UNAVAILABLE_MESSAGE;
  }
  return REVEAL_GENERIC_MESSAGE;
}

export interface Reveal<T> {
  /** Rows currently showing their full values. */
  shown: ReadonlySet<string>;
  /** Rows waiting on the reveal call. */
  loading: ReadonlySet<string>;
  /** The inline error of a row whose last reveal failed. */
  errors: ReadonlyMap<string, string>;
  /** The revealed values of a shown row. */
  values: ReadonlyMap<string, T>;
  toggle: (id: string) => void;
}

export function useReveal<T>(fetchOne: (id: string) => Promise<T>): Reveal<T> {
  const onUnauthorized = useOnUnauthorized();
  const fetchRef = useRef(fetchOne);
  fetchRef.current = fetchOne;
  const [shown, setShown] = useState<ReadonlySet<string>>(new Set());
  const [loading, setLoading] = useState<ReadonlySet<string>>(new Set());
  const [errors, setErrors] = useState<ReadonlyMap<string, string>>(new Map());
  const [values, setValues] = useState<ReadonlyMap<string, T>>(new Map());
  // Ids in flight, read synchronously so a double click asks once.
  const inFlight = useRef(new Set<string>());

  const toggle = useCallback(
    (id: string) => {
      if (inFlight.current.has(id)) return;
      if (shown.has(id)) {
        setShown((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        return;
      }
      setErrors((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      if (values.has(id)) {
        setShown((prev) => new Set(prev).add(id));
        return;
      }
      inFlight.current.add(id);
      setLoading((prev) => new Set(prev).add(id));
      fetchRef.current(id).then(
        (value) => {
          inFlight.current.delete(id);
          setValues((prev) => new Map(prev).set(id, value));
          setShown((prev) => new Set(prev).add(id));
          setLoading((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        },
        (err: unknown) => {
          inFlight.current.delete(id);
          if (isUnauthorized(err)) onUnauthorized();
          setErrors((prev) => new Map(prev).set(id, revealMessage(err)));
          setLoading((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        },
      );
    },
    [shown, values, onUnauthorized],
  );

  return { shown, loading, errors, values, toggle };
}
